import { Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
import { customPositionCellStyle, customPositionRowStyle } from '../../components/postionsWidget/BasketWisePostionContainer'
import useTimeWisePositions from '../../hooks/useTimeWisePositions'
import TimeWisePositions from './timeWisePositions'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { selectSimulatorOrdersLiveData } from '../../selectors/options'
import { calculateGcd } from '../../util/orders'
import { formatGreeks } from '../home/optionChainViewTabs/components/DynamicColumnRender'
import { toLocaleStringIn } from '../../util/general'
import { OrderDataPrices, OrdersDataType } from '../../reducer/orders.reducer'
import { findSimulatorSquareOffOrder } from '../../util/options'
import { useCombineBasketAnalyzeAndLegOrders } from '../../hooks/useCombineBasketAnalyzeAndLegOrders'
import { selectSimulatorDate } from '../../selectors/simulator'

type Props = {
    showAll: boolean
    showGreeks: boolean
}

const PositionsContainer = ({ showAll, showGreeks }: Props) => {
    const positions = useTimeWisePositions()
    const ordersLiveData = useSelector(selectSimulatorOrdersLiveData)
    const { orders } = useCombineBasketAnalyzeAndLegOrders()
    const simulatorTimestamp = useSelector(selectSimulatorDate)

    const requiredPositions = useMemo(() => {
        const newPositions: Map<number, {
            order: OrdersDataType;
            orderPrice: OrderDataPrices;
        }[]> = new Map()
        positions.forEach((value, key) => {
            newPositions.set(key, showAll ? value : value.filter(p => {
                const sqOrder = findSimulatorSquareOffOrder(p.order, orders, simulatorTimestamp)
                return !sqOrder
            }))
        })
        return newPositions
    }, [orders, positions, showAll, simulatorTimestamp])

    const StrategyQuantity = useMemo(() => {
        const arr: number[] = []
        requiredPositions.forEach((position) => {
            if (position.length) {
                arr.push(calculateGcd(
                    position.map((p) => {
                        return p.order.quantity * (p.order.security?.lotSize ?? 0)
                    })
                ))
            }
        })
        return calculateGcd(arr)
    }, [requiredPositions])

    const StrategyAvgPrice = useMemo(() => {
        let result = 0
        requiredPositions.forEach((position) => {
            position.forEach((p) => {
                if (p.order.type === 'buy') {
                    result -= (Number(p.orderPrice?.price ?? 0) * p.order.quantity * (p.order.security?.lotSize ?? 0))
                } else {
                    result += (Number(p.orderPrice?.price ?? 0) * p.order.quantity * (p.order.security?.lotSize ?? 0))
                }
            })
        })
        if (StrategyQuantity === 0) return '0'
        result /= StrategyQuantity
        return toLocaleStringIn(result)
    }, [StrategyQuantity, requiredPositions])

    const StrategyQuantityRender = useMemo(() => {
        if (showGreeks) {
            let delta = 0
            requiredPositions.forEach((position) => {
                position.forEach((p) => {
                    const sqOrder = findSimulatorSquareOffOrder(p.order, orders, simulatorTimestamp)
                    if (!sqOrder) {
                        const orderLiveData = ordersLiveData[p.order.uuid]
                        if (orderLiveData) {
                            delta += (orderLiveData.optionGreeks?.delta ?? 0) * (p.order.quantity * (p.order.security?.lotSize ?? 1))
                        }
                    }
                })
            })
            return formatGreeks(delta)
        }
        return StrategyQuantity
    }, [showGreeks, StrategyQuantity, requiredPositions, orders, simulatorTimestamp, ordersLiveData])

    const StrategyAvgPriceRender = useMemo(() => {
        if (showGreeks) {
            let theta = 0
            requiredPositions.forEach((position) => {
                position.forEach((p) => {
                    const sqOrder = findSimulatorSquareOffOrder(p.order, orders, simulatorTimestamp)
                    if (!sqOrder) {
                        const orderLiveData = ordersLiveData[p.order.uuid]
                        if (orderLiveData) {
                            theta += (orderLiveData.optionGreeks?.theta ?? 0) * (p.order.quantity * (p.order.security?.lotSize ?? 1))
                        }
                    }
                })
            })
            return formatGreeks(theta)
        }
        return StrategyAvgPrice
    }, [showGreeks, StrategyAvgPrice, requiredPositions, orders, simulatorTimestamp, ordersLiveData])

    const StrategyLtpPrice = useMemo(() => {
        if (showGreeks) {
            let gamma = 0
            requiredPositions.forEach((position) => {
                position.forEach((p) => {
                    const sqOrder = findSimulatorSquareOffOrder(p.order, orders, simulatorTimestamp)
                    if (!sqOrder) {
                        const orderLiveData = ordersLiveData[p.order.uuid]
                        if (orderLiveData) {
                            gamma += (orderLiveData.optionGreeks?.gamma ?? 0) * (p.order.quantity * (p.order.security?.lotSize ?? 1))
                        }
                    }
                })
            })
            return formatGreeks(gamma)
        }
        let result = 0
        requiredPositions.forEach((position) => {
            position.forEach((p) => {
                const orderLiveData = ordersLiveData[p.order.uuid]
                if (orderLiveData) {
                    if (p.order.type === 'buy') {
                        result -= (orderLiveData.ltp * p.order.quantity * (p.order.security?.lotSize ?? 0)) / (p.order.security?.priceMultiplier ?? 100)
                    } else {
                        result
                            += (orderLiveData.ltp * p.order.quantity * (p.order.security?.lotSize ?? 0)) / (p.order.security?.priceMultiplier ?? 100)
                    }
                }
            })
        })
        if (StrategyQuantity === 0) return '0'
        result /= StrategyQuantity
        return toLocaleStringIn(result)
    }, [showGreeks, requiredPositions, StrategyQuantity, orders, simulatorTimestamp, ordersLiveData])

    const strategyProfitLoss = useMemo(() => {
        if (showGreeks) {
            let vega = 0
            requiredPositions.forEach((position) => {
                position.forEach((p) => {
                    const sqOrder = findSimulatorSquareOffOrder(p.order, orders, simulatorTimestamp)
                    if (!sqOrder) {
                        const orderLiveData = ordersLiveData[p.order.uuid]
                        if (orderLiveData) {
                            vega += (orderLiveData.optionGreeks?.vega ?? 0) * (p.order.quantity * (p.order.security?.lotSize ?? 1))
                        }
                    }
                })
            })
            return vega
        }
        let profit = 0
        requiredPositions.forEach((position) => {
            position.forEach((p) => {
                const orderLiveData = ordersLiveData[p.order.uuid]
                if (orderLiveData) {
                    const squareOffPrice = (orderLiveData?.ltp ?? 0) / (p.order.security?.priceMultiplier ?? 100)
                    const pnlValue = (Number(squareOffPrice ?? 0) - Number(p.orderPrice?.price ?? 0)) * (p.order.quantity * (p.order.security?.lotSize ?? 0))
                    profit += (p.order.type === 'buy' ? pnlValue : -pnlValue)
                }
            })
        })
        return profit
    }, [showGreeks, requiredPositions, orders, simulatorTimestamp, ordersLiveData])

    return positions.size === 0 ? null : <Table stickyHeader sx={{
        borderCollapse: 'collapse',
        tableLayout: 'auto'
    }}>
        <TableHead>
            <TableRow sx={customPositionRowStyle}>
                <TableCell sx={{ width: '5%', padding: 0, textAlign: 'center' }}>
                </TableCell>
                <TableCell style={{ padding: '5px' }}>
                    <Typography variant="caption" color={'textSecondary'}>SYMBOL</Typography>
                </TableCell>
                <TableCell sx={{ width: '5%', padding: 0, textAlign: 'center' }}>
                    <Typography variant="caption" color={'textSecondary'}>B/S</Typography>
                </TableCell>
                <TableCell style={customPositionCellStyle}>
                    <Typography variant="caption"
                        color={'textSecondary'}>{showGreeks ? 'DELTA' : 'QTY'}</Typography>
                </TableCell>
                <TableCell style={customPositionCellStyle}>
                    <Typography variant="caption"
                        color={'textSecondary'}>{showGreeks ? 'THETA' : 'AVG'}</Typography>
                </TableCell>
                <TableCell style={customPositionCellStyle}>
                    <Typography variant="caption"
                        color={'textSecondary'}>{showGreeks ? 'GAMMA' : 'LTP/SQP'}</Typography>
                </TableCell>
                <TableCell style={customPositionCellStyle}>
                    <Typography variant="caption"
                        color={'textSecondary'}>{showGreeks ? 'VEGA' : 'P&L'}</Typography>
                </TableCell>
            </TableRow>
        </TableHead>
        <TableBody className="font-feature-num">
            {
                [...positions].map(([key, position]) => {
                    if (!showAll && position.every(p => p.order.sqOffByOrderId)) return null
                    return <TimeWisePositions showAll={showAll} timeStamp={key} position={position} showGreeks={showGreeks} />
                })
            }
            <TableRow sx={customPositionRowStyle}>
                <TableCell sx={{ width: '5%', padding: 0, textAlign: 'center' }}>
                </TableCell>
                <TableCell style={{ padding: '5px', fontWeight: 400 }}>
                    TOTAL
                </TableCell>
                <TableCell sx={{ width: '5%', padding: 0, textAlign: 'center' }}>
                    <Typography variant="button"
                        className={`${Number(StrategyAvgPrice) <= 0 ? 'green-text' : 'red-text'}`}>
                        {Number(StrategyAvgPrice) < 0 ? 'B' : Number(StrategyAvgPrice) > 0 ? 'S' : ''}
                    </Typography>
                </TableCell>
                <TableCell style={customPositionCellStyle}>
                    {StrategyQuantityRender}
                </TableCell>
                <TableCell style={customPositionCellStyle}>
                    {showGreeks ? formatGreeks(Number(StrategyAvgPriceRender)) : toLocaleStringIn(Math.abs(Number(StrategyAvgPriceRender)))}
                </TableCell>
                <TableCell style={customPositionCellStyle}>
                    {showGreeks ? formatGreeks(Number(StrategyLtpPrice)) : toLocaleStringIn(Number(StrategyAvgPrice) <= 0 ? (-1 * Number(StrategyLtpPrice)) : Number(StrategyLtpPrice))}
                </TableCell>
                <TableCell style={customPositionCellStyle} className={`${showGreeks ? '' : strategyProfitLoss < 0 ? 'red-text' : strategyProfitLoss > 0 ? 'green-text' : ''}`}>
                    {showGreeks ? formatGreeks(strategyProfitLoss) : strategyProfitLoss === 0 ? '-' : toLocaleStringIn(strategyProfitLoss)}
                </TableCell>
            </TableRow>
        </TableBody>
    </Table>
}

export default PositionsContainer